import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiClient from '../../services/apiClient';
import { API_ENDPOINTS } from '../../constants/apiEndpoints';

export const getPermissions = createAsyncThunk(
    'permissions/getPermissions',
    async (params = {}, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            console.log('Fetching permissions...');

            const response = await apiClient.get(API_ENDPOINTS.PERMISSIONS_ALL, { params });
            console.log('Permissions response:', response.data);

            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Permissions retrieved successfully',
                    data: data
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to fetch permissions',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('Get permissions error:', error);

            let errorMessage = 'Failed to fetch permissions';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to fetch permissions';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const getPermissionCategories = createAsyncThunk(
    'permissions/getCategories',
    async (_, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            console.log('Fetching permission categories...');

            const response = await apiClient.get(API_ENDPOINTS.PERMISSIONS_CATEGORIES);
            console.log('Permission categories response:', response.data);

            const { status, message, data } = response.data;

            if (status === 200) {
                let categories = [];
                let categoryDisplayNames = {};
                let stats = data?.stats || {};

                if (data) {
                    if (data.categories && typeof data.categories === 'object' && !Array.isArray(data.categories)) {
                        categories = Object.keys(data.categories);
                        categoryDisplayNames = data.categories;
                        console.log('Category display names:', categoryDisplayNames);
                    }
                    else if (Array.isArray(data.categories)) {
                        categories = data.categories;
                    }
                    else if (typeof data === 'object' && !Array.isArray(data)) {
                        if (data.by_category && typeof data.by_category === 'object') {
                            categories = Object.keys(data.by_category);
                            stats = data;
                        } else {
                            for (const key in data) {
                                if (typeof data[key] === 'string' || Array.isArray(data[key])) {
                                    if (typeof data[key] === 'string') {
                                        categoryDisplayNames[key] = data[key];
                                        categories.push(key);
                                    }
                                }
                            }
                        }
                    }
                }

                if (categories.length === 0 && stats.by_category) {
                    if (Array.isArray(stats.by_category)) {
                        categories = stats.by_category.map(item => 
                            typeof item === 'object' ? item.category || item.name || Object.keys(item)[0] : item
                        );
                    } else if (typeof stats.by_category === 'object') {
                        categories = Object.keys(stats.by_category);
                    }
                }

                return {
                    success: true,
                    message: message || 'Categories retrieved successfully',
                    categories: categories,
                    categoryDisplayNames: categoryDisplayNames,
                    stats: stats
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to fetch categories',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('Get categories error:', error);

            let errorMessage = 'Failed to fetch categories';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to fetch categories';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const getPermissionStats = createAsyncThunk(
    'permissions/getStats',
    async (_, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            console.log('Fetching permission stats...');

            const response = await apiClient.get(API_ENDPOINTS.PERMISSIONS_STATS);
            console.log('Permission stats response:', response.data);

            const { status, message, data } = response.data;

            if (status === 200) {
                let stats = data || {};
                let categories = [];

                if (stats.by_category) {
                    if (Array.isArray(stats.by_category)) {
                        categories = stats.by_category.map(item => 
                            typeof item === 'object' ? item.category || item.name || Object.keys(item)[0] : item
                        );
                    } else if (typeof stats.by_category === 'object') {
                        categories = Object.keys(stats.by_category);
                    }
                }

                return {
                    success: true,
                    message: message || 'Stats retrieved successfully',
                    stats: stats,
                    categories: categories
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to fetch stats',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('Get stats error:', error);

            let errorMessage = 'Failed to fetch stats';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to fetch stats';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const getPermissionsByCategory = createAsyncThunk(
    'permissions/getByCategory',
    async ({ category, params = {} }, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            console.log('Fetching permissions for category:', category);

            const response = await apiClient.get(API_ENDPOINTS.PERMISSIONS_BY_CATEGORY(category), { params });
            console.log('Permissions by category response:', response.data);

            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Permissions retrieved successfully',
                    data: data,
                    category: category
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to fetch permissions',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('Get permissions by category error:', error);

            let errorMessage = 'Failed to fetch permissions';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to fetch permissions';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const getPermission = createAsyncThunk(
    'permissions/getPermission',
    async (id, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            console.log('Fetching permission:', id);

            const response = await apiClient.get(API_ENDPOINTS.PERMISSION(id));
            console.log('Permission response:', response.data);

            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Permission retrieved successfully',
                    permission: data
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to fetch permission',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('Get permission error:', error);

            let errorMessage = 'Failed to fetch permission';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to fetch permission';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const createPermission = createAsyncThunk(
    'permissions/createPermission',
    async (permissionData, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            console.log('Creating permission...');

            const response = await apiClient.post(API_ENDPOINTS.CREATE_PERMISSION, permissionData);
            console.log('Create permission response:', response.data);

            const { status, message, data } = response.data;

            if (status === 201 || status === 200) {
                return {
                    success: true,
                    message: message || 'Permission created successfully',
                    permission: data
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to create permission',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('Create permission error:', error);

            let errorMessage = 'Failed to create permission';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to create permission';
                
                if (serverData.errors) {
                    const errorMessages = Object.values(serverData.errors).flat().join(', ');
                    errorMessage = errorMessages || errorMessage;
                }
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const updatePermission = createAsyncThunk(
    'permissions/updatePermission',
    async ({ id, data }, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            console.log('Updating permission:', id);

            const response = await apiClient.post(API_ENDPOINTS.UPDATE_PERMISSION(id), data);
            console.log('Update permission response:', response.data);

            const { status, message, data: responseData } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Permission updated successfully',
                    permission: responseData
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to update permission',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('Update permission error:', error);

            let errorMessage = 'Failed to update permission';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to update permission';
                
                if (serverData.errors) {
                    const errorMessages = Object.values(serverData.errors).flat().join(', ');
                    errorMessage = errorMessages || errorMessage;
                }
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const deletePermission = createAsyncThunk(
    'permissions/deletePermission',
    async (id, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            console.log('Deleting permission:', id);

            const response = await apiClient.delete(API_ENDPOINTS.DELETE_PERMISSION(id));
            console.log('Delete permission response:', response.data);

            const { status, message } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Permission deleted successfully',
                    id: id
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to delete permission',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('Delete permission error:', error);

            let errorMessage = 'Failed to delete permission';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to delete permission';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const getUserPermissions = createAsyncThunk(
    'permissions/getUserPermissions',
    async (userId, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            console.log('Fetching user permissions for user:', userId);

            const response = await apiClient.get(API_ENDPOINTS.USER_PERMISSIONS(userId));
            console.log('User permissions response:', response.data);

            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'User permissions retrieved successfully',
                    data: data
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to fetch user permissions',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('Get user permissions error:', error);

            let errorMessage = 'Failed to fetch user permissions';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to fetch user permissions';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const getRolePermissions = createAsyncThunk(
    'permissions/getRolePermissions',
    async (roleId, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            console.log('Fetching role permissions for role:', roleId);

            const response = await apiClient.get(API_ENDPOINTS.ROLE_PERMISSIONS(roleId));
            console.log('Role permissions response:', response.data);

            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Role permissions retrieved successfully',
                    data: data
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to fetch role permissions',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('Get role permissions error:', error);

            let errorMessage = 'Failed to fetch role permissions';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to fetch role permissions';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const syncUserPermissions = createAsyncThunk(
    'permissions/syncUserPermissions',
    async ({ userId, permissions }, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            console.log('Syncing permissions for user:', userId);

            const response = await apiClient.post(API_ENDPOINTS.SYNC_USER_PERMISSIONS, {
                user_id: userId,
                permissions: permissions
            });
            console.log('Sync user permissions response:', response.data);

            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'User permissions updated successfully',
                    data: data
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to sync user permissions',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('Sync user permissions error:', error);

            let errorMessage = 'Failed to sync user permissions';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to sync user permissions';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const syncUserPermissionsBySlugs = createAsyncThunk(
    'permissions/syncUserPermissionsBySlugs',
    async ({ userId, permissions }, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            console.log('Syncing permissions (slugs) for user:', userId);

            const response = await apiClient.post(API_ENDPOINTS.SYNC_USER_PERMISSIONS_SLUGS, {
                user_id: userId,
                permissions: permissions
            });
            console.log('Sync user permissions by slugs response:', response.data);

            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'User permissions updated successfully',
                    data: data
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to sync user permissions',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('Sync user permissions by slugs error:', error);

            let errorMessage = 'Failed to sync user permissions';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to sync user permissions';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const syncRolePermissions = createAsyncThunk(
    'permissions/syncRolePermissions',
    async ({ roleId, permissions }, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            console.log('Syncing permissions for role:', roleId);

            const response = await apiClient.post(API_ENDPOINTS.SYNC_ROLE_PERMISSIONS(roleId), {
                permissions: permissions
            });
            console.log('Sync role permissions response:', response.data);

            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Role permissions updated successfully',
                    data: data
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to sync role permissions',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('Sync role permissions error:', error);

            let errorMessage = 'Failed to sync role permissions';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to sync role permissions';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const syncRolePermissionsBySlugs = createAsyncThunk(
    'permissions/syncRolePermissionsBySlugs',
    async ({ roleId, permissions }, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            console.log('Syncing permissions (slugs) for role:', roleId);

            const response = await apiClient.post(API_ENDPOINTS.SYNC_ROLE_PERMISSIONS_SLUGS(roleId), {
                permissions: permissions
            });
            console.log('Sync role permissions by slugs response:', response.data);

            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Role permissions updated successfully',
                    data: data
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to sync role permissions',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('Sync role permissions by slugs error:', error);

            let errorMessage = 'Failed to sync role permissions';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to sync role permissions';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

const initialState = {
    permissions: [],
    pagination: {
        current_page: 1,
        last_page: 1,
        per_page: 15,
        total: 0
    },
    categories: [],
    categoryDisplayNames: {}, 
    stats: {
        total: 0,
        by_category: {},
        by_level: {},
        system_count: 0
    },
    currentPermission: null,
    isLoading: false,
    error: null,
    successMessage: null,
    apiMessage: null,
};

const permissionSlice = createSlice({
    name: 'permissions',
    initialState,
    reducers: {
        clearError: (state) => {
            state.error = null;
        },
        clearSuccessMessage: (state) => {
            state.successMessage = null;
        },
        clearApiMessage: (state) => {
            state.apiMessage = null;
        },
        clearPermissionState: (state) => {
            state.permissions = [];
            state.pagination = {
                current_page: 1,
                last_page: 1,
                per_page: 15,
                total: 0
            };
            state.categories = [];
            state.categoryDisplayNames = {};
            state.stats = {
                total: 0,
                by_category: {},
                by_level: {},
                system_count: 0
            };
            state.currentPermission = null;
            state.error = null;
            state.successMessage = null;
            state.apiMessage = null;
        },
    },
    extraReducers: (builder) => {
        builder
            // ===== GET PERMISSIONS =====
            .addCase(getPermissions.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(getPermissions.fulfilled, (state, action) => {
                state.isLoading = false;
                state.permissions = action.payload.data?.data || [];
                state.pagination = action.payload.data?.meta || {
                    current_page: 1,
                    last_page: 1,
                    per_page: 15,
                    total: 0
                };
                state.successMessage = action.payload.message;
                state.apiMessage = action.payload.message;
                state.error = null;
            })
            .addCase(getPermissions.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to fetch permissions';
                state.apiMessage = action.payload?.message || 'Failed to fetch permissions';
                state.successMessage = null;
                state.permissions = [];
                state.pagination = {
                    current_page: 1,
                    last_page: 1,
                    per_page: 15,
                    total: 0
                };
            })

            // ===== GET PERMISSION CATEGORIES =====
            .addCase(getPermissionCategories.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(getPermissionCategories.fulfilled, (state, action) => {
                state.isLoading = false;
                state.categories = Array.isArray(action.payload.categories) 
                    ? action.payload.categories 
                    : [];
                state.categoryDisplayNames = action.payload.categoryDisplayNames || {};
                state.stats = action.payload.stats || state.stats;
                state.successMessage = action.payload.message;
                state.apiMessage = action.payload.message;
                state.error = null;
                
                console.log('Categories loaded:', state.categories);
                console.log('Category display names:', state.categoryDisplayNames);
            })
            .addCase(getPermissionCategories.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to fetch categories';
                state.apiMessage = action.payload?.message || 'Failed to fetch categories';
                state.successMessage = null;
                state.categories = [];
                state.categoryDisplayNames = {};
            })

            // ===== GET PERMISSION STATS =====
            .addCase(getPermissionStats.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(getPermissionStats.fulfilled, (state, action) => {
                state.isLoading = false;
                state.stats = action.payload.stats || state.stats;
                
                // If categories came from stats and we don't have them yet
                if (action.payload.categories && action.payload.categories.length > 0 && state.categories.length === 0) {
                    state.categories = action.payload.categories;
                }
                
                state.successMessage = action.payload.message;
                state.apiMessage = action.payload.message;
                state.error = null;
            })
            .addCase(getPermissionStats.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to fetch stats';
                state.apiMessage = action.payload?.message || 'Failed to fetch stats';
                state.successMessage = null;
                state.stats = {
                    total: 0,
                    by_category: {},
                    by_level: {},
                    system_count: 0
                };
            })

            // ===== GET PERMISSION =====
            .addCase(getPermission.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(getPermission.fulfilled, (state, action) => {
                state.isLoading = false;
                state.currentPermission = action.payload.permission;
                state.successMessage = action.payload.message;
                state.apiMessage = action.payload.message;
                state.error = null;
            })
            .addCase(getPermission.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to fetch permission';
                state.apiMessage = action.payload?.message || 'Failed to fetch permission';
                state.successMessage = null;
                state.currentPermission = null;
            })

            // ===== GET PERMISSIONS BY CATEGORY =====
            .addCase(getPermissionsByCategory.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(getPermissionsByCategory.fulfilled, (state, action) => {
                state.isLoading = false;
                state.permissions = action.payload.data?.data || [];
                state.pagination = action.payload.data?.meta || {
                    current_page: 1,
                    last_page: 1,
                    per_page: 15,
                    total: 0
                };
                state.successMessage = action.payload.message;
                state.apiMessage = action.payload.message;
                state.error = null;
            })
            .addCase(getPermissionsByCategory.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to fetch permissions';
                state.apiMessage = action.payload?.message || 'Failed to fetch permissions';
                state.successMessage = null;
                state.permissions = [];
                state.pagination = {
                    current_page: 1,
                    last_page: 1,
                    per_page: 15,
                    total: 0
                };
            })

            // ===== CREATE PERMISSION =====
            .addCase(createPermission.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(createPermission.fulfilled, (state, action) => {
                state.isLoading = false;
                state.successMessage = action.payload.message;
                state.apiMessage = action.payload.message;
                state.error = null;
            })
            .addCase(createPermission.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to create permission';
                state.apiMessage = action.payload?.message || 'Failed to create permission';
                state.successMessage = null;
            })

            // ===== UPDATE PERMISSION =====
            .addCase(updatePermission.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(updatePermission.fulfilled, (state, action) => {
                state.isLoading = false;
                state.successMessage = action.payload.message;
                state.apiMessage = action.payload.message;
                state.error = null;
            })
            .addCase(updatePermission.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to update permission';
                state.apiMessage = action.payload?.message || 'Failed to update permission';
                state.successMessage = null;
            })

            // ===== DELETE PERMISSION =====
            .addCase(deletePermission.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(deletePermission.fulfilled, (state, action) => {
                state.isLoading = false;
                state.successMessage = action.payload.message;
                state.apiMessage = action.payload.message;
                state.error = null;
            })
            .addCase(deletePermission.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to delete permission';
                state.apiMessage = action.payload?.message || 'Failed to delete permission';
                state.successMessage = null;
            })

            // ===== GET USER PERMISSIONS =====
            .addCase(getUserPermissions.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(getUserPermissions.fulfilled, (state, action) => {
                state.isLoading = false;
                state.successMessage = action.payload.message;
                state.apiMessage = action.payload.message;
                state.error = null;
            })
            .addCase(getUserPermissions.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to get user permissions';
                state.apiMessage = action.payload?.message || 'Failed to get user permissions';
                state.successMessage = null;
            })

            // ===== GET ROLE PERMISSIONS =====
            .addCase(getRolePermissions.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(getRolePermissions.fulfilled, (state, action) => {
                state.isLoading = false;
                state.successMessage = action.payload.message;
                state.apiMessage = action.payload.message;
                state.error = null;
            })
            .addCase(getRolePermissions.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to get role permissions';
                state.apiMessage = action.payload?.message || 'Failed to get role permissions';
                state.successMessage = null;
            })

            // ===== SYNC USER PERMISSIONS =====
            .addCase(syncUserPermissions.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(syncUserPermissions.fulfilled, (state, action) => {
                state.isLoading = false;
                state.successMessage = action.payload.message;
                state.apiMessage = action.payload.message;
                state.error = null;
            })
            .addCase(syncUserPermissions.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to sync user permissions';
                state.apiMessage = action.payload?.message || 'Failed to sync user permissions';
                state.successMessage = null;
            })

            // ===== SYNC USER PERMISSIONS BY SLUGS =====
            .addCase(syncUserPermissionsBySlugs.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(syncUserPermissionsBySlugs.fulfilled, (state, action) => {
                state.isLoading = false;
                state.successMessage = action.payload.message;
                state.apiMessage = action.payload.message;
                state.error = null;
            })
            .addCase(syncUserPermissionsBySlugs.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to sync user permissions';
                state.apiMessage = action.payload?.message || 'Failed to sync user permissions';
                state.successMessage = null;
            })

            // ===== SYNC ROLE PERMISSIONS =====
            .addCase(syncRolePermissions.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(syncRolePermissions.fulfilled, (state, action) => {
                state.isLoading = false;
                state.successMessage = action.payload.message;
                state.apiMessage = action.payload.message;
                state.error = null;
            })
            .addCase(syncRolePermissions.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to sync role permissions';
                state.apiMessage = action.payload?.message || 'Failed to sync role permissions';
                state.successMessage = null;
            })

            // ===== SYNC ROLE PERMISSIONS BY SLUGS =====
            .addCase(syncRolePermissionsBySlugs.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(syncRolePermissionsBySlugs.fulfilled, (state, action) => {
                state.isLoading = false;
                state.successMessage = action.payload.message;
                state.apiMessage = action.payload.message;
                state.error = null;
            })
            .addCase(syncRolePermissionsBySlugs.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to sync role permissions';
                state.apiMessage = action.payload?.message || 'Failed to sync role permissions';
                state.successMessage = null;
            });
    },
});

export const {
    clearError,
    clearSuccessMessage,
    clearApiMessage,
    clearPermissionState,
} = permissionSlice.actions;

export const selectPermissions = (state) => state.permissions.permissions || [];
export const selectPermissionPagination = (state) => state.permissions.pagination || {
    current_page: 1,
    last_page: 1,
    per_page: 15,
    total: 0
};
export const selectPermissionCategories = (state) => state.permissions.categories || [];
export const selectCategoryDisplayNames = (state) => state.permissions.categoryDisplayNames || {};
export const selectPermissionStats = (state) => state.permissions.stats || {
    total: 0,
    by_category: {},
    by_level: {},
    system_count: 0
};
export const selectCurrentPermission = (state) => state.permissions.currentPermission;
export const selectPermissionsLoading = (state) => state.permissions.isLoading;
export const selectPermissionsError = (state) => state.permissions.error;
export const selectPermissionsSuccess = (state) => state.permissions.successMessage;
export const selectPermissionsApiMessage = (state) => state.permissions.apiMessage;

export default permissionSlice.reducer;